import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatDate, daysUntil } from '../utils/styles';

const TARGET_USD = 120000;
const TARGET_CONTAINERS = 4;

function StatCard({ label, value, sub, color='#3b82f6', onClick, to, clickLabel }) {
  const nav = useNavigate();
  const handleClick = () => { if (onClick) onClick(); else if (to) nav(to); };
  return (
    <div onClick={handleClick} style={{ background:'#fff', borderRadius:10, padding:'18px 20px',
      border:'1px solid #e5e7eb', borderLeft:`4px solid ${color}`,
      cursor:(onClick||to)?'pointer':'default' }}>
      <div style={{ fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.7, marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:700, color:'#111827' }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:'#9ca3af', marginTop:3 }}>{sub}</div>}
      {(onClick||to) && <div style={{ fontSize:11, color:color, marginTop:4 }}>{clickLabel || 'Click to view →'}</div>}
    </div>
  );
}

function Bar({ value, max, color }) {
  const pct = max > 0 ? (value/max)*100 : 0;
  return (
    <div style={{ height:10, background:'#f3f4f6', borderRadius:5, overflow:'hidden' }}>
      <div style={{ height:'100%', width:`${Math.min(100,pct)}%`, background:color, borderRadius:5 }} />
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [monthOrders, setMonthOrders] = useState({ active:[], dispatched:[] });
  const [monthlyData, setMonthlyData] = useState([]);
  const [showDispatchDetail, setShowDispatchDetail] = useState(false);
  const [showOrdersDrilldown, setShowOrdersDrilldown] = useState(false);
  const [dbSettings, setDbSettings] = useState({ target_monthly_usd: 120000, target_containers: 4 });
  const nav = useNavigate();

  const thisMonth = new Date().toISOString().substring(0,7);

  useEffect(() => {
    api.get('/inventory/analytics/dashboard').then(r => setData(r.data)).catch(()=>{});
    api.get('/orders?status=active').then(r => setAllOrders(r.data)).catch(()=>{});
    api.get('/inventory/analytics/monthly').then(r => setMonthlyData(r.data)).catch(()=>{});
    api.get(`/orders/this-month?month=${thisMonth}`).then(r => setMonthOrders(r.data)).catch(()=>{});
    api.get('/inventory/settings').then(r => setDbSettings(r.data)).catch(()=>{});
  }, []);

  const TARGET_USD = dbSettings.target_monthly_usd || 120000;
  const TARGET_CONTAINERS = dbSettings.target_containers || 4;
  const monthlyVal = data?.monthly_dispatch?.value || 0;
  const monthlyContainers = data?.monthly_dispatch?.containers || 0;
  const dispatchPct = (monthlyVal/TARGET_USD)*100;
  const containerPct = (monthlyContainers/TARGET_CONTAINERS)*100;

  // All orders for this month (active + dispatched)
  const allThisMonth = [...(monthOrders.dispatched||[]), ...(monthOrders.active||[])];
  const thisMonthTotalValue = allThisMonth.reduce((s,o) => s+(o.order_value||0), 0);
  const ordersPct = (thisMonthTotalValue/TARGET_USD)*100;

  const overlay = { position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 };
  const modal = { background:'#fff',borderRadius:12,padding:24,width:700,maxHeight:'80vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.2)' };
  const S = { th:{ padding:'8px 10px', textAlign:'left', fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.5, background:'#f9fafb' }, td:{ padding:'10px 10px', fontSize:13 } };

  // Current month breakdown from monthly data
  const currentMonthData = monthlyData.find(m => m.month === thisMonth);

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:22, fontWeight:600, margin:0 }}>Dashboard</h1>
        <div style={{ fontSize:13, color:'#9ca3af', marginTop:3 }}>{formatDate(new Date().toISOString().split('T')[0])}</div>
      </div>

      {/* Alerts */}
      {data?.overdue_orders?.length > 0 && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
          <div style={{ fontWeight:600, color:'#dc2626', fontSize:13, marginBottom:4 }}>🔴 Overdue shipments</div>
          {data.overdue_orders.map(o => (
            <div key={o.id} style={{ fontSize:12, color:'#b91c1c' }}>
              <Link to={`/orders/${o.id}`} style={{ color:'#b91c1c', fontWeight:500 }}>{o.pi_number}</Link>
              {' '}— {o.company_name} · {Math.round(o.days_overdue)} day{Math.round(o.days_overdue)!==1?'s':''} overdue
            </div>
          ))}
        </div>
      )}
      {data?.upcoming_shipments?.length > 0 && (
        <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
          <div style={{ fontWeight:600, color:'#d97706', fontSize:13, marginBottom:4 }}>🟡 Upcoming shipments (next 14 days)</div>
          {data.upcoming_shipments.map(o => (
            <div key={o.id} style={{ fontSize:12, color:'#92400e' }}>
              <Link to={`/orders/${o.id}`} style={{ color:'#92400e', fontWeight:500 }}>{o.pi_number}</Link>
              {' '}— {o.company_name} · {Math.round(o.days_left)} days · {formatDate(o.expected_ship_date)}
            </div>
          ))}
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
        <StatCard label="Active orders" value={data?.active_orders?.count||0}
          sub={`$${((data?.active_orders?.value||0)).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} total value`}
          color="#3b82f6" to="/orders" clickLabel="View active orders →" />
        <StatCard label="This month dispatched"
          value={`$${monthlyVal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`}
          sub={`Target $120,000 · ${dispatchPct.toFixed(1)}%`}
          color={dispatchPct>=100?'#10b981':dispatchPct>=60?'#f59e0b':'#ef4444'}
          to="/dispatched" clickLabel="View dispatched orders →" />
        <StatCard label="Low stock alerts" value={data?.low_stock?.length||0}
          sub="Below reorder level" color={data?.low_stock?.length>0?'#f59e0b':'#10b981'} to="/inventory" />
        <StatCard label="Pending POs" value={data?.pending_pos?.count||0}
          sub="Awaiting delivery" color="#8b5cf6" to="/purchase-orders" />
      </div>

      {/* Target bars */}
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'18px 20px', marginBottom:18 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:24 }}>

          {/* Dispatched bar - links to dispatched page */}
          <div style={{ cursor:'pointer' }} onClick={() => nav('/dispatched')}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13 }}>
              <span style={{ fontWeight:600 }}>Monthly dispatch target</span>
              <span style={{ color:'#6b7280', fontSize:12 }}>${monthlyVal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} / $120,000</span>
            </div>
            <Bar value={monthlyVal} max={TARGET_USD} color={dispatchPct>=100?'#10b981':dispatchPct>=60?'#3b82f6':'#f59e0b'} />
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
              <span style={{ fontSize:11, color:dispatchPct>=100?'#10b981':'#9ca3af' }}>{dispatchPct.toFixed(1)}% of target{dispatchPct>=100?' ✓':''}</span>
              <span style={{ fontSize:11, color:'#3b82f6' }}>View dispatched →</span>
            </div>
          </div>

          {/* Orders in hand bar */}
          <div style={{ cursor:'pointer' }} onClick={() => setShowOrdersDrilldown(true)}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13 }}>
              <span style={{ fontWeight:600 }}>Orders in hand this month</span>
              <span style={{ color:'#6b7280', fontSize:12 }}>${thisMonthTotalValue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} · {allThisMonth.length} orders</span>
            </div>
            <Bar value={thisMonthTotalValue} max={TARGET_USD} color={ordersPct>=100?'#10b981':ordersPct>=60?'#8b5cf6':'#f97316'} />
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
              <span style={{ fontSize:11, color:ordersPct>=100?'#10b981':'#9ca3af' }}>{ordersPct.toFixed(1)}% of target</span>
              <span style={{ fontSize:11, color:'#3b82f6' }}>Click to see →</span>
            </div>
          </div>

          {/* Containers bar */}
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13 }}>
              <span style={{ fontWeight:600 }}>Containers dispatched</span>
              <span style={{ color:'#6b7280', fontSize:12 }}>{monthlyContainers} / {TARGET_CONTAINERS} target</span>
            </div>
            <Bar value={monthlyContainers} max={TARGET_CONTAINERS} color={containerPct>=100?'#10b981':containerPct>=50?'#3b82f6':'#f59e0b'} />
            <div style={{ fontSize:11, color:containerPct>=100?'#10b981':'#9ca3af', marginTop:4 }}>{containerPct.toFixed(1)}% of target{containerPct>=100?' ✓':''}</div>
          </div>
        </div>
      </div>

      {/* This month table — ALL orders including shipped */}
      {allThisMonth.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:18, marginBottom:18 }}>
          <div style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>
            All orders this month ({allThisMonth.length})
            <span style={{ fontSize:12, color:'#9ca3af', fontWeight:400, marginLeft:8 }}>Expected to ship in {new Date().toLocaleDateString('en-GB',{month:'long',year:'numeric'})}</span>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
              {['PI Number','Customer','Pcs','Value','Ship date','Status'].map(h => (
                <th key={h} style={{ padding:'6px 10px', fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.5, textAlign:'left', background:'#f9fafb' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {/* Dispatched orders first */}
              {(monthOrders.dispatched||[]).map(o => (
                <tr key={`d-${o.id}`} style={{ borderBottom:'1px solid #f3f4f6', background:'#f0fdf4' }}>
                  <td style={{ padding:'9px 10px' }}><Link to={`/orders/${o.id}`} style={{ color:'#3b82f6', fontWeight:600, textDecoration:'none', fontSize:13 }}>{o.pi_number}</Link></td>
                  <td style={{ padding:'9px 10px', fontSize:13 }}>{o.company_name}</td>
                  <td style={{ padding:'9px 10px', fontSize:13 }}>{(o.total_pcs||0).toLocaleString()}</td>
                  <td style={{ padding:'9px 10px', fontSize:13, fontWeight:500 }}>{o.currency} {(o.order_value||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                  <td style={{ padding:'9px 10px', fontSize:13, color:'#374151' }}>{formatDate(o.expected_ship_date)}</td>
                  <td style={{ padding:'9px 10px' }}>
                    <span style={{ background:'#dcfce7', color:'#16a34a', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600 }}>✓ Shipped {formatDate(o.dispatch_date)}</span>
                  </td>
                </tr>
              ))}
              {/* Active orders */}
              {(monthOrders.active||[]).sort((a,b) => (a.expected_ship_date||'').localeCompare(b.expected_ship_date||'')).map(o => {
                const pct = o.total_pcs > 0 ? Math.round((o.total_shipped/o.total_pcs)*100) : 0;
                const days = daysUntil(o.expected_ship_date);
                return (
                  <tr key={`a-${o.id}`} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ padding:'9px 10px' }}><Link to={`/orders/${o.id}`} style={{ color:'#3b82f6', fontWeight:600, textDecoration:'none', fontSize:13 }}>{o.pi_number}</Link></td>
                    <td style={{ padding:'9px 10px', fontSize:13 }}>{o.company_name}</td>
                    <td style={{ padding:'9px 10px', fontSize:13 }}>{(o.total_pcs||0).toLocaleString()}</td>
                    <td style={{ padding:'9px 10px', fontSize:13, fontWeight:500 }}>{o.currency} {(o.order_value||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                    <td style={{ padding:'9px 10px', fontSize:13 }}>
                      <span style={{ fontWeight:600, color:days!==null&&days<0?'#ef4444':days!==null&&days<=7?'#f97316':'#374151' }}>
                        {formatDate(o.expected_ship_date)}
                      </span>
                      {days !== null && <span style={{ fontSize:11, color:'#9ca3af', marginLeft:6 }}>{days<0?`${Math.abs(days)}d overdue`:days===0?'Today!':days<=14?`${days}d`:''}</span>}
                    </td>
                    <td style={{ padding:'9px 10px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <div style={{ width:50, height:5, background:'#e5e7eb', borderRadius:3, overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background:'#3b82f6' }} />
                        </div>
                        <span style={{ fontSize:11, color:'#6b7280' }}>{pct}% shipped</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18 }}>
        {/* Active orders list */}
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:18 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:15 }}>Active orders</div>
            <Link to="/orders/new" style={{ padding:'5px 12px', background:'#3b82f6', color:'#fff', borderRadius:6, textDecoration:'none', fontSize:12, fontWeight:500 }}>+ New order</Link>
          </div>
          {allOrders.length === 0 ? (
            <div style={{ color:'#9ca3af', fontSize:13, textAlign:'center', padding:'20px 0' }}>No active orders</div>
          ) : [...allOrders].sort((a,b) => (a.expected_ship_date||'9999').localeCompare(b.expected_ship_date||'9999')).slice(0,8).map(o => {
            const days = daysUntil(o.expected_ship_date);
            return (
              <Link key={o.id} to={`/orders/${o.id}`} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'1px solid #f3f4f6', textDecoration:'none', color:'inherit' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:500 }}>{o.pi_number}</div>
                  <div style={{ fontSize:11, color:'#9ca3af' }}>{o.company_name} · {(o.total_pcs||0).toLocaleString()} pcs</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:12, color:'#3b82f6', fontWeight:500 }}>${(o.order_value||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                  {o.expected_ship_date && (
                    <div style={{ fontSize:11, color:days!==null&&days<0?'#ef4444':days!==null&&days<=7?'#f97316':'#9ca3af', fontWeight:days!==null&&days<=7?600:400 }}>
                      {formatDate(o.expected_ship_date)}{days!==null&&days<=14?` (${days<0?Math.abs(days)+'d late':days+'d'})`:''}</div>
                  )}
                </div>
              </Link>
            );
          })}
          {allOrders.length > 8 && <Link to="/orders" style={{ fontSize:12, color:'#3b82f6', display:'block', marginTop:10, textDecoration:'none' }}>View all {allOrders.length} orders →</Link>}
        </div>

        {/* Stock alerts */}
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:18 }}>
          <div style={{ fontWeight:600, fontSize:15, marginBottom:14 }}>Stock alerts</div>
          {!data?.low_stock?.length ? (
            <div style={{ color:'#10b981', fontSize:13, textAlign:'center', padding:'20px 0' }}>✓ All stock levels OK</div>
          ) : data.low_stock.map(m => (
            <div key={m.id} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'1px solid #f3f4f6' }}>
              <div>
                <div style={{ fontSize:13, fontWeight:500 }}>{m.name}</div>
                <div style={{ fontSize:11, color:'#9ca3af' }}>{[m.spec1,m.spec2].filter(Boolean).join(' · ')}</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:12, color:'#f59e0b', fontWeight:500 }}>{(m.current_stock-m.assigned_stock).toFixed(0)} {m.unit} free</div>
                <div style={{ fontSize:11, color:'#9ca3af' }}>Min: {m.reorder_level}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Orders drilldown modal */}
      {showOrdersDrilldown && (
        <div style={overlay} onClick={() => setShowOrdersDrilldown(false)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <div style={{ fontWeight:600, fontSize:15 }}>Orders this month — all ({allThisMonth.length})</div>
              <button onClick={() => setShowOrdersDrilldown(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#9ca3af' }}>×</button>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>{['PI','Customer','Value','Ship date','Status'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {(monthOrders.dispatched||[]).map(o => (
                  <tr key={o.id} style={{ borderBottom:'1px solid #f3f4f6', background:'#f0fdf4' }}>
                    <td style={S.td}><Link to={`/orders/${o.id}`} onClick={()=>setShowOrdersDrilldown(false)} style={{ color:'#3b82f6', fontWeight:600, textDecoration:'none' }}>{o.pi_number}</Link></td>
                    <td style={S.td}>{o.company_name}</td>
                    <td style={{ ...S.td, color:'#10b981', fontWeight:500 }}>${(o.order_value||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                    <td style={S.td}>{formatDate(o.expected_ship_date)}</td>
                    <td style={S.td}><span style={{ background:'#dcfce7', color:'#16a34a', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600 }}>✓ Shipped</span></td>
                  </tr>
                ))}
                {(monthOrders.active||[]).map(o => (
                  <tr key={o.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={S.td}><Link to={`/orders/${o.id}`} onClick={()=>setShowOrdersDrilldown(false)} style={{ color:'#3b82f6', fontWeight:600, textDecoration:'none' }}>{o.pi_number}</Link></td>
                    <td style={S.td}>{o.company_name}</td>
                    <td style={{ ...S.td, fontWeight:500 }}>${(o.order_value||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                    <td style={S.td}>{formatDate(o.expected_ship_date)}</td>
                    <td style={S.td}><span style={{ background:'#eff6ff', color:'#3b82f6', padding:'2px 8px', borderRadius:20, fontSize:11 }}>Active</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop:12, padding:'10px 0', borderTop:'1px solid #e5e7eb', display:'flex', justifyContent:'space-between', fontSize:13, fontWeight:700 }}>
              <span>Total</span>
              <span style={{ color:'#3b82f6' }}>${thisMonthTotalValue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
